<template>
    <div class="magic-extractor-page">
        <!-- 柔光背景 -->
        <div class="magic-bg"></div>

        <!-- 页面标题 -->
        <div class="page-header">
            <h1>🎧 视频转 MP3 提取器</h1>
            <p>支持在线链接提取或上传本地 MP4 文件</p>
        </div>

        <!-- 上传区域卡片 -->
        <div class="upload-card">
            <div class="card-icon">
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
            </div>

            <div class="upload-area" @dragover.prevent @drop.prevent="handleDrop">
                <input
                        type="file"
                        ref="fileInput"
                        accept=".mp4,video/mp4"
                        @change="handleFileSelect"
                        style="display: none"
                />

                <p v-if="!selectedFile">
                    📁 拖拽 MP4 文件到这里，或
                    <a href="#" @click.prevent="$refs.fileInput.click()">点击选择文件</a>
                </p>

                <div v-else class="file-info">
                    <strong>已选择：</strong> {{ selectedFile.name }}
                    <br />
                    <small>大小：{{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB</small>
                    <button class="btn-process" @click="startConversion" :disabled="isProcessing">
                        {{ isProcessing ? '转换中...' : '开始提取' }}
                    </button>
                </div>
            </div>

            <!-- 状态标签 -->
            <div class="tag status-tag">{{ status }}</div>
        </div>

        <!-- 进度条 -->
        <div v-if="progress > 0" class="progress-container">
            <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progress + '%' }"></div>
            </div>
            <p>🔄 处理进度：{{ progress }}%</p>
        </div>

        <!-- 结果展示 -->
        <div v-if="audioBlob" class="result-card">
            <h3>✅ 提取完成</h3>
            <div class="audio-player">
                <audio controls :src="audioUrl" style="width: 100%"></audio>
            </div>
            <button class="download-btn" @click="downloadMP3">
                ⬇️ 下载 MP3 文件
            </button>
        </div>

        <!-- 底部提示 -->
        <div class="footer-tip">
            <small>🔒 所有处理均在本地进行，文件不会上传到任何服务器</small>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>🎵 视频提取 MP3 音频工具</h2>
            <p>从 MP4、WebM、MOV 等视频文件中提取音频，保存为高质量 MP3 格式。支持本地文件和在线视频链接，适合提取背景音乐、保存视频旁白、制作音频素材。</p>

            <h3>✨ 功能特点</h3>
            <ul>
                <li><strong>格式广泛</strong>：支持 MP4、WebM、MOV、AVI 等常见视频格式</li>
                <li><strong>高质量输出</strong>：128kbps 标准音质，清晰无杂音</li>
                <li><strong>双模式</strong>：支持上传本地文件或粘贴在线视频链接</li>
                <li><strong>快速处理</strong>：使用 FFmpeg.wasm 高效解码</li>
            </ul>

            <h3>📝 使用教程</h3>
            <ol>
                <li>选择「上传文件」或「在线链接」</li>
                <li>上传视频或粘贴视频 URL</li>
                <li>等待提取完成（根据视频长度需几秒到几分钟）</li>
                <li>下载 MP3 音频文件</li>
            </ol>

            <h3>💡 适用场景</h3>
            <p><strong>提取 BGM</strong>：保存视频中的背景音乐<br>
            <strong>播客制作</strong>：提取采访视频的音频部分<br>
            <strong>语言学习</strong>：保存外语视频的音频反复听<br>
            <strong>素材收集</strong>：积累音效、配乐素材</p>

            <h3>🔒 隐私说明</h3>
            <p>使用 FFmpeg.wasm 在浏览器本地处理，视频不会上传到服务器。</p>
        </div>
    </div>
</template>

<script>
import seoMixin from '../js/seo-mixin';
    import { createFFmpeg } from '@ffmpeg/ffmpeg'

    export default {
        name: 'MP4ToMP3Extractor',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '视频提取MP3音频在线工具 - FreeToolBox',
                seoDescription: '免费在线视频转MP3工具，从MP4视频中提取音频。支持上传本地文件或在线链接，128k高质量输出，浏览器本地处理，保护隐私。',
                seoKeywords: '视频提取音频,mp4转mp3,视频转音频,在线提取mp3,音频提取工具',
                selectedFile: null,
                isProcessing: false,
                progress: 0,
                status: '待上传',
                audioBlob: null,
                audioUrl: '',
                ffmpeg: null
            }
        },
        async mounted() {
            // 初始化 ffmpeg
            this.ffmpeg = createFFmpeg({
                log: true,
                corePath: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/ffmpeg-core.js',
                workerPath: 'https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.12.6/dist/ffmpeg-worker.js',
                progress: ({ ratio }) => {
                    this.progress = Math.round(ratio * 100)
                }
            })
            await this.ffmpeg.load()
            this.status = '就绪'
        },
        methods: {
            handleDrop(e) {
                const file = e.dataTransfer.files[0]
                if (file && file.type === 'video/mp4') {
                    this.selectedFile = file
                    this.audioBlob = null
                    this.audioUrl = ''
                    this.progress = 0
                }
            },
            handleFileSelect(e) {
                const file = e.target.files[0]
                if (file && file.type === 'video/mp4') {
                    this.selectedFile = file
                    this.audioBlob = null
                    this.audioUrl = ''
                    this.progress = 0
                } else {
                    this.showToast('❌ 请上传有效的 MP4 文件', 'error')
                }
            },
            async startConversion() {
                if (!this.selectedFile || !this.isFFmpegReady) return;

                this.isProcessing = true;
                this.progress = 0;
                this.status = '处理中...';

                try {
                    // ✅ 使用 FileReader 读取文件内容（替代 fetchFile）
                    const arrayBuffer = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result);
                        reader.onerror = () => reject(reader.error);
                        reader.readAsArrayBuffer(this.selectedFile);
                    });

                    // 写入 FFmpeg 虚拟文件系统
                    this.ffmpeg.FS('writeFile', 'input.mp4', new Uint8Array(arrayBuffer));

                    // 执行转换命令
                    await this.ffmpeg.run('-i', 'input.mp4', '-vn', '-c:a', 'libmp3lame', '-b:a', '128k', 'output.mp3');

                    // 读取输出文件
                    const data = this.ffmpeg.FS('readFile', 'output.mp3');
                    this.audioBlob = new Blob([data.buffer], { type: 'audio/mpeg' });
                    this.audioUrl = URL.createObjectURL(this.audioBlob);

                    // 清理虚拟文件系统
                    this.ffmpeg.FS('unlink', 'input.mp4');
                    this.ffmpeg.FS('unlink', 'output.mp3');

                    this.status = '提取成功';
                    this.showToast('🎉 音频提取完成', 'success');
                } catch (err) {
                    console.error('转换失败:', err);
                    this.showToast(`❌ 转换失败: ${err.message}`, 'error');
                    this.status = '转换失败';
                } finally {
                    this.isProcessing = false;
                }
            },
            downloadMP3() {
                if (!this.audioBlob) return

                const link = document.createElement('a')
                link.href = this.audioUrl
                link.download = this.selectedFile.name.replace(/\.(mp4|avi|mov|mkv)$/i, '.mp3')
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                this.showToast('⬇️ MP3 文件已下载', 'info')
            },
            showToast(msg, type = 'info') {
                const old = document.querySelector('.magic-toast')
                if (old) old.remove()

                const toast = document.createElement('div')
                toast.className = 'magic-toast'
                toast.textContent = msg
                const colors = { success: '#52c41a', error: '#ff4d4f', info: '#1890ff' }
                toast.style.cssText = `
        position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
        background: ${colors[type]}; color: white; padding: 12px 24px; border-radius: 8px;
        font-size: 14px; font-weight: 500; z-index: 99999; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        transition: opacity 0.3s; text-align: center; min-width: 200px;
      `
                document.body.appendChild(toast)
                setTimeout(() => {
                    toast.style.opacity = '0'
                    setTimeout(() => toast.remove(), 300)
                }, 2000)
            }
        },
        beforeUnmount() {
            if (this.audioUrl) {
                URL.revokeObjectURL(this.audioUrl)
            }
        }
    }
</script>

<style scoped>
    .magic-extractor-page {
        min-height: 100vh;
        padding: 40px 20px;
        position: relative;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        color: #333;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .magic-bg {
        display: none;
    }

    .page-header {
        text-align: center;
        margin-bottom: 40px;
    }

    .page-header h1 {
        font-size: 2.4em;
        margin: 0;
        font-weight: 700;
        background: linear-gradient(90deg, #a188ff, #00d4ff);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .page-header p {
        color: #aaa;
        margin-top: 8px;
        font-size: 1em;
    }

    /* 上传卡片 */
    .upload-card {
        max-width: 800px;
        margin: 0 auto;
        background: rgba(30, 32, 50, 0.8);
        border-radius: 16px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        padding: 24px;
        display: flex;
        align-items: center;
        position: relative;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(8px);
    }

    .card-icon {
        width: 50px;
        height: 50px;
        background: linear-gradient(135deg, #a188ff, #64b5f6);
        border-radius: 12px;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5em;
        margin-right: 16px;
        flex-shrink: 0;
    }

    .upload-area {
        flex: 1;
        padding: 20px;
        border: 2px dashed #4a4a70;
        border-radius: 12px;
        text-align: center;
        color: #aaa;
        cursor: pointer;
        transition: all 0.3s;
    }

    .upload-area:hover {
        border-color: #7a6aff;
        background: rgba(122, 106, 255, 0.05);
    }

    .upload-area a {
        color: #7a6aff;
        text-decoration: underline;
    }

    .file-info {
        text-align: left;
    }

    .btn-process {
        margin-top: 10px;
        padding: 8px 16px;
        background: linear-gradient(135deg, #7a6aff, #00c6ff);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
    }

    .btn-process:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .tag.status-tag {
        position: absolute;
        top: 12px;
        right: 12px;
        background: rgba(255, 255, 255, 0.2);
        color: #aaa;
        font-size: 0.75em;
        padding: 4px 10px;
        border-radius: 12px;
    }

    /* 进度条 */
    .progress-container {
        max-width: 800px;
        margin: 20px auto;
        color: #e0e0ff;
    }

    .progress-bar {
        width: 100%;
        height: 6px;
        background: #333;
        border-radius: 3px;
        overflow: hidden;
    }

    .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #7a6aff, #00c6ff);
        transition: width 0.3s;
    }

    /* 结果卡片 */
    .result-card {
        max-width: 800px;
        margin: 30px auto 0;
        padding: 20px;
        background: rgba(30, 32, 50, 0.6);
        border-radius: 12px;
        color: #e0e0ff;
        line-height: 1.8;
        border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .download-btn {
        margin-top: 16px;
        padding: 12px 24px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 16px;
        cursor: pointer;
        width: 100%;
    }

    .download-btn:hover {
        transform: translateY(-2px);
    }

    .footer-tip {
        text-align: center;
        margin-top: 60px;
        color: #777;
        font-size: 0.9em;
    }
    /* 内容说明区样式 */
    .tool-content {
        max-width: 800px;
        margin: 40px auto;
        padding: 30px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: left;
    }
    .tool-content h2 {
        color: #333;
        font-size: 1.5em;
        margin-bottom: 15px;
        border-bottom: 2px solid #ff5722;
        padding-bottom: 10px;
    }
    .tool-content h3 {
        color: #555;
        font-size: 1.2em;
        margin: 20px 0 10px;
    }
    .tool-content p, .tool-content li {
        color: #666;
        line-height: 1.8;
        margin-bottom: 10px;
    }
    .tool-content ul, .tool-content ol {
        padding-left: 20px;
        margin-bottom: 15px;
    }
</style>